import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SearchOutlined, WarningOutlined } from '@ant-design/icons';
import Illustration from 'components/common/illustration';
import styled from 'types/theme.types';
import { typeStyle } from 'constants/typography';

export const tableEmptyStateTypes = ['no-data', 'no-results', 'error'] as const;
export type TableEmptyStateType = (typeof tableEmptyStateTypes)[number];

// `no-results` and `error` keep the Table page's icons (304:167 / 304:174) — they describe a search
// or a failure, not missing data. No `isNewDesign` branch: only the new-design path renders it.
const ICONS: Record<Exclude<TableEmptyStateType, 'no-data'>, React.ReactNode> = {
  'no-results': <SearchOutlined />,
  error: <WarningOutlined />
};

type TableEmptyStateProps = {
  type: TableEmptyStateType;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  'data-e2e'?: string;
};

// No title line: the description alone says what is missing (review feedback - the "No data yet"
// heading over a specific description was noise), so callers fold the fact into the description.
export const TableEmptyState: React.FunctionComponent<TableEmptyStateProps> = ({
  type,
  description,
  action,
  className,
  'data-e2e': dataE2e
}) => (
  <Container className={className} data-e2e={dataE2e}>
    {/* Missing data always draws the design's empty-state artwork (210:8458). */}
    {type === 'no-data' ? (
      <StyledIllustration name="empty-state" data-e2e={dataE2e && `${dataE2e}-illustration`} />
    ) : (
      <IconContainer isError={type === 'error'} data-e2e={dataE2e && `${dataE2e}-icon`}>
        {ICONS[type]}
      </IconContainer>
    )}
    <Description>{description ?? <FormattedMessage id={`table.empty.${type}.description`} />}</Description>
    {action && <Action>{action}</Action>}
  </Container>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  padding: ${({ theme }) => theme.padding.xxxl} 0;
`;

/* The container's 4px gap sets the description under the artwork; a control needs a full step away. */
const Action = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const StyledIllustration = styled(Illustration)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const IconContainer = styled.div<{ isError: boolean }>`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  svg {
    font-size: 32px;
    color: ${({ isError }) => (isError ? 'var(--icon-error)' : 'var(--icon-secondary)')};
  }
`;

const Description = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-secondary);
  /* The copy carries authored line breaks now that it also carries the what's-missing fact. */
  white-space: pre-line;
`;

export default TableEmptyState;
